import { useState } from 'react';
import { ChevronDown, Pencil, ShieldCheck } from 'lucide-react';
import Card from './Card';
import { SECONDARY_BTN } from './helper';

// A member and their insurance. The two do not run side by side: the membership
// is what applies while it is active, so using a plan means ending it first —
// and that is the practice's decision, not a button here.
const Shell = ({ title, body, children = null }) => (
  <div className="rounded-xl border border-gray-200 bg-secondary-50/60 p-4">
    <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
      <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-blue-100 bg-white text-teamcare-blue">
        <ShieldCheck size={20} />
      </span>

      <div className="min-w-0 flex-1">
        <p className="text-[15px] font-bold text-secondary-900">{title}</p>
        <p className="mt-1 text-xs leading-relaxed text-secondary-500">{body}</p>
      </div>

      {children && <div className="flex shrink-0 items-center gap-2">{children}</div>}
    </div>
  </div>
);

// No insurance on file, and no way to add one while the membership is live.
// Deliberately has no action: "Cancel membership" on the panel above is the
// only route, and offering a second one would start a change the patient
// cannot finish.
//
// A covered family member cannot even do that — the API refuses their cancel —
// so telling them to cancel first sends them at a button they do not have. Name
// the person who can instead.
export const InsuranceNeedsCancelling = ({ manageable = true, subscriberName = null }) => (
  <Shell
    title="Want to use dental insurance?"
    body={
      manageable
        ? "Cancel your membership first. The practice will confirm when your member pricing ends — you can add your plan once it has."
        : `${subscriberName || 'Whoever pays for this membership'} bought this membership, so only they can cancel it. Ask them to, and you can add your plan once the practice confirms your member pricing has ended.`
    }
  />
);

// On file, but not in force: the membership is what applies until it ends. The
// card lives behind a toggle rather than taking the top of the step.
const MemberInsurance = ({ attributes, memberName, onUpdate, disabled = false,
                          manageable = true, subscriberName = null }) => {
  const [open, setOpen] = useState(false);
  const carrier = attributes?.insurance_company?.data?.attributes?.title;

  return (
    <>
      <Shell
        title={carrier ? `${carrier} is on file` : 'Your insurance is on file'}
        body={
          manageable
            ? "Your membership is what applies while it's active. Cancel it and the practice will confirm when your member pricing ends — your plan takes over from then."
            : `The membership ${subscriberName || 'someone in your household'} bought is what applies while it's active. Only they can cancel it — your plan takes over once the practice confirms your member pricing has ended.`
        }
      >
        <button type="button" onClick={() => setOpen(!open)} aria-expanded={open} className={`${SECONDARY_BTN} gap-1.5`}>
          {open ? 'Hide' : 'View'}
          <ChevronDown size={15} strokeWidth={2.5} className={open ? 'rotate-180' : ''} />
        </button>

        <button type="button" onClick={onUpdate} disabled={disabled} className={`${SECONDARY_BTN} gap-1.5`}>
          <Pencil size={14} strokeWidth={2.5} />
          Update
        </button>
      </Shell>

      {open && (
        <div className="mx-auto w-full max-w-lg">
          <Card data={attributes} memberName={memberName} />
        </div>
      )}
    </>
  );
};

export default MemberInsurance;
